"use client";

import { expansionClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Program Benefits 1044 — BDU "Learn More".
 *
 * Port of BDU v1 `components/Degree/LearnMore.tsx`: a GREY band (`bg-gray-400`,
 * #CBCBCB — not an off-white one) with a heading and a rule, then a stack of white
 * cards holding rich-text copy. A card whose content is taller than 96px is clamped to
 * 90px and gains a Read more / View less toggle — measured on the real DOM node,
 * exactly as in the reference.
 *
 * TWO GRADIENTS, and they are NOT the same colour. The rule under the heading is BDU's
 * blue `bg-fee-header` (gradient 1/2); the Read more toggle is red GRADIENT TEXT
 * (gradient 3/4, `bg-gradient-to-r from-red-25 to-red-50`), not a flat colour.
 *
 * BREAKPOINTS — v1 `md:` (768px) is `tab:` here (this app remaps `md:` to 1230px).
 *
 * GRADIENT TEXT — `bg-clip-text` alone leaves the toggle INVISIBLE in WebKit because
 * this app's Lightning CSS pipeline emits no `-webkit-` prefixes, so
 * `WebkitBackgroundClip` is set inline as well.
 *
 * The reference hardcodes the heading "Learn More"; it is a CMS field here because
 * every other BDU section heading already is, and the section is reused per programme.
 */

interface HighlightRow {
  id?: string | number;
  meta_title?: string;
  /** Rich text (HTML) */
  meta_description?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface ProgramBenefits1044Data {
  metadata?: HighlightRow[];
  title?: string;
  title_start_color?: string;
  /** Blue rule under the heading */
  gradient_color_1?: string;
  gradient_color_2?: string;
  /** Red gradient for the Read more / View less toggle */
  gradient_color_3?: string;
  gradient_color_4?: string;
  /** Band behind the cards */
  desktop_background_color?: string;
  /** Card surface, border and body copy */
  card_hover_background_color?: string;
  subtitle_color?: string;
  description_color?: string;
  /** Read more / View less toggle label */
  load_more_button?: string;
  status?: boolean;
}

/** The reference's collapsed height (90px) and its toggle threshold (96px). */
const CLAMP_PX = 90;
const TOGGLE_THRESHOLD_PX = 96;

const ProgramBenefits1044 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as
    | ProgramBenefits1044Data
    | undefined;

  const {
    title = "Learn More",
    title_start_color = "#222023",
    gradient_color_1 = "#1F3D56",
    gradient_color_2 = "#7CB5E6",
    gradient_color_3 = "#F60214",
    gradient_color_4 = "#A10113",
    desktop_background_color = "#CBCBCB",
    card_hover_background_color = "#FFFFFF",
    subtitle_color = "#E2E8F0",
    description_color = "#222023",
    load_more_button = "Read more",
  } = sectionData || {};

  const highlights = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [];
    return rows
      .filter((r) => r?.meta_status !== false)
      .sort(
        (a, b) =>
          (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [showToggle, setShowToggle] = useState<boolean[]>([]);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);

  const measure = useCallback(() => {
    setShowToggle(
      highlights.map((_, index) => {
        const el = contentRefs.current[index];
        return el ? el.scrollHeight > TOGGLE_THRESHOLD_PX : false;
      }),
    );
  }, [highlights]);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  if (!sectionData?.status || highlights.length === 0) return null;

  return (
    <div
      className="mx-auto size-full w-full px-5 py-9 tab:px-16 tab:py-12"
      style={{ backgroundColor: desktop_background_color }}
    >
      <div className="mb-8 flex flex-col tab:gap-2">
        <h2
          className="text-[24px] font-bold tab:text-[28px]"
          style={{ color: title_start_color }}
        >
          {title}
        </h2>
        <div
          className="h-[2px] w-[114px] tab:h-[3px] tab:w-[148px]"
          style={{
            backgroundImage: `linear-gradient(90deg, ${gradient_color_1} 0%, ${gradient_color_2} 100%)`,
          }}
        />
      </div>

      <div className="flex w-full flex-col items-center justify-between gap-3">
        {highlights.map((highlight, index) => {
          const isExpanded = expandedIndex === index;
          const isToggleable = showToggle[index];

          return (
            <div
              key={highlight.id ?? index}
              // The reference's shadcn Card: bordered, shadowed, white — with
              // `rounded-md` overriding the Card's own `rounded-xl`.
              className="relative w-full rounded-md border shadow px-4 py-6 tab:px-10"
              style={{
                backgroundColor: card_hover_background_color,
                borderColor: subtitle_color,
              }}
            >
              {highlight.meta_title && (
                <h3
                  className="text-[20px] font-semibold leading-[120%]"
                  style={{ color: description_color }}
                >
                  {highlight.meta_title}
                </h3>
              )}
              <div
                className="text-[14px] font-normal leading-[160%]"
                style={{ color: description_color }}
              >
                {/*
                  Deliberately NOT `.rich-text-content`. That helper forces
                  `p { font-weight: … !important }` and re-sizes headings with
                  `!important`, so the h2s inside this copy would jump to the site
                  heading scale and the body would lose its weight. The reference opts
                  into list styling with these arbitrary variants alone and lets
                  preflight flatten everything else — same here.
                */}
                <div
                  ref={(el) => {
                    contentRefs.current[index] = el;
                  }}
                  className={cn(
                    "h-auto overflow-hidden",
                    "[&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5 [&_ul_li]:mt-2",
                    "[&_a]:text-inherit [&_a]:underline",
                  )}
                  style={
                    isToggleable && !isExpanded
                      ? { maxHeight: `${CLAMP_PX}px` }
                      : undefined
                  }
                  dangerouslySetInnerHTML={{
                    __html: highlight.meta_description || "",
                  }}
                />

                {isToggleable && (
                  <button
                    type="button"
                    onClick={() => {
                      setExpandedIndex((prev) =>
                        prev === index ? null : index,
                      );
                      expansionClick({
                        section: "program_benefits_1044",
                        component: "learn_more",
                        label: load_more_button,
                      });
                    }}
                    aria-expanded={isExpanded}
                    aria-label={isExpanded ? "View less" : load_more_button}
                    className="inline-block cursor-pointer bg-clip-text text-[14px] font-semibold text-transparent"
                    style={{
                      backgroundImage: `linear-gradient(to right, ${gradient_color_3}, ${gradient_color_4})`,
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                    }}
                  >
                    {isExpanded ? "View less" : load_more_button}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProgramBenefits1044;
